/**
 * WorkStoppageHistoryTable
 *
 * The traçabilité of one arrêt, read-only by construction: nothing in the UI can edit or
 * delete a history row, which is what makes it worth reading. Shared by the details drawer
 * and the deleted-arrêts dialog — a deleted arrêt's trail is the same rows, the last of
 * them being its deletion.
 *
 * Also exports the formatting helpers and the table frame both screens draw with.
 */
import React from "react";
import { Chip, Table, TableBody, TableCell, TableRow, Typography, Box } from "@mui/material";
import moment from "moment";
import Langs, { Localize } from "../../app/lang/langs";
import { statusLabelKey } from "./WorkStoppageStatus";

export const NAVY = "rgb(0, 40, 128)";

export const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "—");

export const formatAmount = (value) =>
  value === null || typeof value === "undefined" || value === ""
    ? "—"
    : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(value));

/** The payment and history tables, framed like the form's inputs. */
export const tableFrameSx = {
  mx: 1,
  mb: 1,
  border: "1.5px solid rgba(0, 40, 128, 0.08)",
  borderRadius: "12px",
  overflowX: "auto",
  "& .MuiTableCell-head": {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: NAVY,
    bgcolor: "rgba(0, 40, 128, 0.03)",
  },
  "& .MuiTableRow-root:last-of-type .MuiTableCell-body": { borderBottom: "none" },
};

/**
 * The traçabilité stores raw column names and raw values (`cpam_days_overridden: 1 → 0`).
 * This is the translation key naming each column the API can log; a column missing here
 * falls back to its raw name rather than to nothing.
 */
const HISTORY_FIELD_LABELS = {
  id_planning: "WORK_STOPPAGE_LEAVE",
  id_collab: "COLLAB",
  id_absence_reference: "WORK_STOPPAGE_MOTIF",
  custom_motif: "WORK_STOPPAGE_CUSTOM_MOTIF",
  id_parent: "WORK_STOPPAGE_PARENT",
  stoppage_status: "WORK_STOPPAGE_STATUS",
  start_date: "START_DATE",
  end_date: "END_DATE",
  received_at: "WORK_STOPPAGE_RECEIVED_AT",
  declared_at: "WORK_STOPPAGE_DECLARED_AT",
  subrogation: "WORK_STOPPAGE_SUBROGATION",
  salary_maintenance: "WORK_STOPPAGE_SALARY_MAINTENANCE",
  carence_days: "WORK_STOPPAGE_CARENCE_DAYS",
  cpam_days: "WORK_STOPPAGE_CPAM_DAYS",
  cpam_days_overridden: "WORK_STOPPAGE_CPAM_DAYS_OVERRIDDEN",
  therapeutic_rate: "WORK_STOPPAGE_THERAPEUTIC_RATE",
  maintenance_category: "WORK_STOPPAGE_MAINTENANCE_CATEGORY",
  maintenance_days_100: "WORK_STOPPAGE_MAINTENANCE_100",
  maintenance_days_80: "WORK_STOPPAGE_MAINTENANCE_REDUCED",
  maintenance_days_unpaid: "WORK_STOPPAGE_MAINTENANCE_UNPAID",
  maintenance_overridden: "WORK_STOPPAGE_MAINTENANCE_OVERRIDDEN",
  cpam_reimbursement_status: "WORK_STOPPAGE_CPAM_REIMBURSEMENT_STATUS",
  prevoyance_reimbursement_status: "WORK_STOPPAGE_PREVOYANCE_REIMBURSEMENT_STATUS",
  // Not a column: logged when declaring the suivi validates a collab's pending request.
  planning_status: "WORK_STOPPAGE_PLANNING_STATUS",
  comments: "Comments",
  // Payment rows
  source: "WORK_STOPPAGE_PAYMENT_SOURCE",
  payment_date: "WORK_STOPPAGE_PAYMENT_DATE",
  period_from: "WORK_STOPPAGE_PERIOD_FROM",
  period_to: "WORK_STOPPAGE_PERIOD_TO",
  amount: "WORK_STOPPAGE_AMOUNT",
};

const HISTORY_BOOLEAN_FIELDS = ["subrogation", "salary_maintenance", "cpam_days_overridden", "maintenance_overridden"];
const HISTORY_DATE_FIELDS = ["start_date", "end_date", "received_at", "declared_at", "payment_date", "period_from", "period_to"];

/**
 * One logged value, as a person reads it. The API writes values as strings, NULL as null.
 * @param {Array} reference - the référentiel, to name a motif rather than show its id
 */
const formatHistoryValue = (field, value, reference) => {
  if (value === null || typeof value === "undefined" || value === "") return "—";
  if (HISTORY_BOOLEAN_FIELDS.includes(field)) return Localize(Number(value) ? "YES" : "NO");
  if (HISTORY_DATE_FIELDS.includes(field)) return formatDate(value);
  if (field === "id_absence_reference") {
    const motif = (reference || []).find((row) => String(row.id) === String(value));
    return motif ? motif.label : `#${value}`;
  }
  if (field === "id_parent" || field === "id_planning") return `#${value}`;
  if (field === "stoppage_status" || field === "cpam_reimbursement_status" || field === "prevoyance_reimbursement_status")
    return Localize(statusLabelKey(value));
  if (field === "source") return value === "PREVOYANCE" ? Localize("WORK_STOPPAGE_PREVOYANCE") : "CPAM";
  if (field === "planning_status") return Localize(`WORK_STOPPAGE_LEAVE_STATUS_${value}`);
  if (field === "amount") return formatAmount(value);
  if (field === "therapeutic_rate") return `${Number(value)} %`;
  return value;
};

/**
 * @param {Object} props
 * @param {Array} props.history - work_stoppages_history rows, newest first
 * @param {Array} props.reference - the référentiel, to name motifs
 */
export default function WorkStoppageHistoryTable({ history, reference }) {
  return (
    <Box sx={tableFrameSx}>
      <Table size="small">
        <TableBody>
          {(history || []).map((entry) => (
            <TableRow key={entry.id}>
              <TableCell width={160}>
                <Typography variant="caption" color="text.secondary">
                  {moment(entry.created_at).format("DD/MM/YYYY HH:mm")}
                </Typography>
              </TableCell>
              <TableCell width={200}>
                <Chip
                  size="small"
                  label={Localize(`WORK_STOPPAGE_ACTION_${entry.action}`)}
                  sx={{ fontWeight: 600, borderRadius: "8px" }}
                />
              </TableCell>
              <TableCell>
                {entry.field ? (
                  <>
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                      {HISTORY_FIELD_LABELS[entry.field] ? Localize(HISTORY_FIELD_LABELS[entry.field]) : entry.field}
                    </Typography>
                    {` : ${formatHistoryValue(entry.field, entry.old_value, reference)}`}
                    {` → ${formatHistoryValue(entry.field, entry.new_value, reference)}`}
                  </>
                ) : ""}
              </TableCell>
              <TableCell width={180}>
                <Typography variant="caption" color="text.secondary">{entry.createdBy}</Typography>
              </TableCell>
            </TableRow>
          ))}
          {!(history || []).length && (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.6 }}>
                  <Langs str="WORK_STOPPAGE_NO_HISTORY" />
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
